import districtDefaultIcon from '@/assets/district_default_icon.svg';
import locationCityIcon from '@/assets/location_city.png';
import mySchoolsIcon from '@/assets/my_schools.svg';
import schoolDefaultIcon from '@/assets/school_default_icon.svg';
import { AccessibleDivButton, FlyerboardFilters } from '@/components';
import { APP_ROUTES, FlyerType, SubscriptionTypeEnum } from '@/config/constants';
import colors from '@/theme/colors';
import { Subscription } from '@/types/subscriptions';
import scrollToTop from '@/utils/scroolToTop';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TuneIcon from '@mui/icons-material/Tune';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import AuthenticationContext from '@/context/AuthenticationContext';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { GET_DISTRICT, GET_SCHOOL } from '@/graphql/queries';
import {
  districtVar,
  flyerboardActiveFiltersVar,
  flyerboardFlyersAmountVar,
} from '@/graphql/state';
import { GetDistrictResponse, GetSchoolResponse } from '@/graphql/types';
import { useAnalytics, useChildGrades, useNavigateWithTracking, useURLSearchParams } from '@/hooks';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import DateFnsUtils from '@date-io/date-fns';
import { Tag } from '../Tag';
import { BodyLarge, BodyLargeEmphasized, BodyMediumMidEmphasisUnderline } from '../Typography';
import styles from './FlyerboardHeader.styles';

interface Props {
  subscriptions: Subscription[];
  type: string;
  selectedAudienceId: string;
  trackingEventFrom: string;
}

export const FlyerboardHeader: React.FC<Props> = ({
  subscriptions,
  type: activeTab,
  selectedAudienceId,
  trackingEventFrom,
}) => {
  const { community } = useContext(UserPreferencesContext);
  const { isAuthenticated } = useContext(AuthenticationContext);
  const navigateWithTracking = useNavigateWithTracking();
  const [options, setOptions] = useState<Subscription[]>([]);
  const [selectedOption, setSelectedOption] = useState<Subscription>();
  const [open, setOpen] = useState(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const isSchool = activeTab === FlyerType.school;
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const hasMultipleSchools = isSchool && subscriptions.length > 1;
  const hasOptions = isSchool && isAuthenticated;
  const [getSchoolById] = useLazyQuery<GetSchoolResponse>(GET_SCHOOL);
  const [getDistrictById] = useLazyQuery<GetDistrictResponse>(GET_DISTRICT);
  const dateFns = new DateFnsUtils();
  const { trackEvent } = useAnalytics();
  const { audienceType, categoryId } = useURLSearchParams();
  const district = useReactiveVar(districtVar);
  const { grades } = useChildGrades();

  const trackingFrom = useMemo(
    () => ({
      event_from: TrackingEvents.viewFlyerSet,
      event_detail_from: trackingEventFrom,
      stage_from: TrackingStage.discovery,
    }),
    [trackingEventFrom]
  );

  const checkHistoricalFilters = () => {
    const activeFlyerboard = window.location.href;
    const previousFlyerboard = localStorage.getItem('activeFlyerboard');
    if (previousFlyerboard && previousFlyerboard !== activeFlyerboard) {
      clearHistoryFilters();
      return;
    }
    localStorage.setItem('activeFlyerboard', activeFlyerboard);

    const updateAt = localStorage.getItem('filtersUpdateAt');
    if (updateAt) {
      const currentDate = new Date();
      const differenceInMinutes = dateFns.getDiff(currentDate, new Date(updateAt), 'minutes');
      if (differenceInMinutes < 60) {
        const historycalFilter = localStorage.getItem('flyerboardFilters');
        if (historycalFilter) {
          const filterParsed = JSON.parse(historycalFilter);
          flyerboardActiveFiltersVar(filterParsed);
        }
      } else {
        clearHistoryFilters();
      }
    }
  };

  const clearHistoryFilters = () => {
    localStorage.removeItem('filtersUpdateAt');
    localStorage.removeItem('flyerboardFilters');
    localStorage.removeItem('activeFlyerboard');
    flyerboardActiveFiltersVar({
      grades: [],
      types: [],
    });
    flyerboardFlyersAmountVar(0);
  };

  const mySchoolsOption = useMemo(
    () => ({
      id: 'all',
      name: 'My Schools',
      logo: mySchoolsIcon,
      type: SubscriptionTypeEnum.school,
    }),
    []
  );

  const loadOptions = useCallback(() => {
    if (subscriptions.length > 0) {
      if (isSchool) {
        if (hasOptions && district?.name) {
          const districtOption = {
            id: district.districtId.toString(),
            name: district.name,
            logo: districtDefaultIcon,
            type: SubscriptionTypeEnum.district,
          };

          if (hasMultipleSchools) {
            setOptions([...subscriptions, { ...mySchoolsOption }, { ...districtOption }]);
          } else {
            setOptions([...subscriptions, { ...districtOption }]);
          }
        } else {
          setOptions([]);
        }
      } else {
        setOptions([]);
      }
    }
  }, [subscriptions, isSchool, hasOptions, hasMultipleSchools, district, mySchoolsOption]);

  const defineSelectedOption = () => {
    if (isSchool) {
      if (options.length > 0) {
        const option = options.find(
          opt =>
            opt.id === selectedAudienceId &&
            (audienceType !== SubscriptionTypeEnum.community ? opt.type === audienceType : true)
        );
        const currentOption = option || options[0];
        setSelectedOption(currentOption);
      } else if (subscriptions.length > 0) {
        const option = subscriptions[0];
        setSelectedOption(option);
      }
    } else if (community) {
      setSelectedOption({
        type: SubscriptionTypeEnum.community,
        id: '1',
        name: `${community.localArea}`,
        logo: locationCityIcon,
      });
    }
  };

  const getSchool = useCallback(async () => {
    try {
      const { data } = await getSchoolById({
        variables: {
          schoolId: Number(selectedAudienceId),
        },
      });

      if (data?.getSchool) {
        const { schoolId, name, districtId } = data?.getSchool;
        if (district?.districtId !== districtId.toString()) {
          districtVar({ districtId: districtId.toString() });
        }
        setSelectedOption({
          id: schoolId.toString(),
          name,
          type: SubscriptionTypeEnum.school,
        });
      }
    } catch (err) {
      console.log(err);
    }
  }, [district]);

  const getDistrict = useCallback(async () => {
    try {
      const { data } = await getDistrictById({
        variables: {
          districtId: Number(selectedAudienceId),
        },
      });

      if (data?.getDistrict) {
        const { districtId, name } = data.getDistrict;
        setSelectedOption({
          id: districtId.toString(),
          name,
          logo: districtDefaultIcon,
          type: SubscriptionTypeEnum.district,
        });
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (selectedAudienceId === 'all') {
        getDistrict();
      } else if (audienceType === SubscriptionTypeEnum.school) {
        getSchool();
      } else if (audienceType === SubscriptionTypeEnum.district) {
        getDistrict();
      }
    } else if (district) {
      loadOptions();
    }
  }, [
    subscriptions,
    audienceType,
    district,
    loadOptions,
    getDistrict,
    getSchool,
    isSchool,
    isAuthenticated,
  ]);

  useEffect(() => {
    defineSelectedOption();
  }, [options]);

  useEffect(() => {
    checkHistoricalFilters();
  }, []);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const onSelectOption = (option: Subscription) => {
    trackEvent({
      event: TrackingEvents.filterFlyerSet,
      event_detail: EventDetails.filterFlyerSet('schools'),
      stage: TrackingStage.discovery,
      ...trackingFrom,
    });

    setSelectedOption(option);
    setOpen(false);
    const { id, type } = option;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('audienceId', id);
    newSearchParams.set('audienceType', type);
    setSearchParams(newSearchParams, { replace: true });
    scrollToTop();
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const amountSelectedFilters = useMemo(() => {
    const { grades: selectedGrades = [], types: selectedTypes = [] } = flyerboardActiveFilters;
    const isGradesFilterActive = selectedGrades.length > 0 && selectedGrades.length < grades.length;
    const gradeFilterCount = isGradesFilterActive ? 1 : 0;
    const typeFilterCount = selectedTypes.length;
    return gradeFilterCount + typeFilterCount;
  }, [flyerboardActiveFilters, grades.length]);

  const getIcon = useCallback(() => {
    if (selectedOption?.logo) return selectedOption.logo;
    return selectedOption?.type === 'school' ? schoolDefaultIcon : districtDefaultIcon;
  }, [selectedOption]);

  const editCommunity = () => {
    const event = 'edit_community';
    trackEvent({
      event,
      event_detail: event,
      stage: TrackingStage.discovery,
      ...trackingFrom,
    });
    navigateWithTracking(APP_ROUTES.accountInformation, trackingFrom, {
      state: { expandSection: 'community-information' },
    });
  };

  return (
    <>
      <div className={styles.container}>
        <div>
          <div className={styles.buttonWrapper}>
            <Button
              ref={anchorRef}
              className={styles.button}
              id="composition-button"
              aria-controls={open ? 'composition-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              onClick={handleToggle}
              disabled={options.length === 0}
            >
              {selectedOption && (
                <img src={getIcon()} alt={selectedOption.name} width="24px" height="24px" />
              )}
              <BodyLargeEmphasized nonTranslate className={styles.activeOption}>
                {selectedOption?.name}
              </BodyLargeEmphasized>
              {hasOptions && <ArrowDropDownIcon sx={{ color: colors.prussian }} />}
            </Button>
            {selectedOption?.type === FlyerType.community && (
              <AccessibleDivButton role="link" onClick={editCommunity}>
                <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                  Edit
                </BodyMediumMidEmphasisUnderline>
              </AccessibleDivButton>
            )}
          </div>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            placement="bottom-start"
            transition
            disablePortal
            className={styles.popper}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                  borderRadius: '12px',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList
                      autoFocusItem={open}
                      id="composition-menu"
                      aria-labelledby="composition-button"
                      role={undefined}
                      // eslint-disable-next-line react/jsx-no-bind
                      onKeyDown={handleListKeyDown}
                    >
                      {options.map(option => (
                        <MenuItem
                          className={styles.menuItem}
                          selected={
                            selectedOption?.id === option.id && selectedOption?.type === option.type
                          }
                          disableRipple
                          onClick={() => onSelectOption(option)}
                          key={`${option.id}-${option.type}`}
                        >
                          <div role="button" tabIndex={0} className={styles.optionMenu}>
                            <img
                              src={option?.logo || schoolDefaultIcon}
                              alt={option.name}
                              width="24px"
                              height="24px"
                            />
                            {selectedOption?.id === option.id &&
                            selectedOption?.type === option.type ? (
                              <BodyLargeEmphasized nonTranslate className={styles.optionText}>
                                {option.name}
                              </BodyLargeEmphasized>
                            ) : (
                              <BodyLarge nonTranslate className={styles.optionText}>
                                {option.name}
                              </BodyLarge>
                            )}
                          </div>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
        {!categoryId && (
          <AccessibleDivButton onClick={() => setShowFilter(true)}>
            <Tag
              variant={amountSelectedFilters === 0 ? 'outline' : 'active'}
              rightIcon={<TuneIcon sx={{ fontSize: '15px' }} />}
              className={styles.tagFilter}
              nonTranslate
            >
              {amountSelectedFilters > 0 ? (
                <span key="badge" className={styles.badge}>
                  {amountSelectedFilters}
                </span>
              ) : null}
            </Tag>
          </AccessibleDivButton>
        )}
      </div>
      <FlyerboardFilters
        trackId="filters"
        flyerType={activeTab}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        trackingEventFrom={trackingEventFrom}
      />
    </>
  );
};
